import React from 'react';
import '../streamBuilders.css';

/**
 * The request bar of the WebSocket, SSE and gRPC builders — the HTTP builder's bar:
 * one bordered field group (`.request-url-field-group`) holding [leading] [input]
 * [trailing], and the primary action where HTTP has Send (`.request-url-bar__send`).
 *
 * The builders used to bring their own bars (a loose input, a status word, a pill
 * button) — three looks beside the HTTP one. `inputProps.className` keeps the builders'
 * existing hooks (`ws-builder__url-input` …) so tests and recordings still find them.
 *
 * @param {object} props
 * @param {React.ReactNode} [props.leading]   inside the group, before the input (method picker)
 * @param {React.ReactNode} [props.trailing]  inside the group, after the input (gRPC method picker)
 * @param {{status: string, label: string}} [props.state]  dot + word at the end of the group
 * @param {{label: string, onClick: Function, disabled?: boolean, danger?: boolean, className?: string}} props.action
 */
export default function StreamRequestBar({ leading = null, trailing = null, state = null, inputProps, action }) {
  const { className: inputClassName = '', ...restInput } = inputProps;
  return (
    <div className="stream-bar flex-shrink-0 px-5 pt-3 pb-2">
      <div className="flex items-stretch gap-2 min-w-0 w-full">
        <div className="request-url-field-group url-container stream-bar__group flex flex-1 min-w-0 items-stretch rounded-lg border border-gray-200 overflow-hidden bg-white">
          {leading && <div className="stream-bar__leading flex flex-shrink-0 items-stretch border-r border-gray-200">{leading}</div>}
          <input type="text" spellCheck={false} {...restInput} className={`stream-bar__input flex-1 min-w-0 ${inputClassName}`} />
          {state && (
            <span className={`stream-bar__state stream-bar__state--${state.status}`} role="status">
              <span className="stream-bar__dot" aria-hidden />
              {state.label}
            </span>
          )}
          {trailing && <div className="stream-bar__trailing flex flex-shrink-0 items-stretch border-l border-gray-200">{trailing}</div>}
        </div>

        <div className="request-url-bar__send-group flex flex-row items-stretch flex-shrink-0 self-stretch rounded-lg overflow-hidden">
          <button
            type="button"
            onClick={action.onClick}
            disabled={action.disabled}
            className={`request-url-bar__send stream-bar__action${action.danger ? ' stream-bar__action--danger' : ''} inline-flex items-center px-3 py-1.5 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-xs font-semibold border-0 rounded-none ${action.className || ''}`}
          >
            {action.label}
          </button>
        </div>
      </div>
    </div>
  );
}
